import {useScrollableFocus} from '../../components/reception/useScrollableFocus';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useToast } from '../../ui/hooks';
import { ReceptionSidebar } from '../../components/reception-nav/ReceptionSidebar';
import DailyNotificationModal from './components/DailyNotificationModal';
import { QUERY_META } from '../../queries/core/queryMeta';
import { receptionKeys } from '../../queries/reception/receptionKeys';
import { fetchReceptionDailySummaryResponse } from '../../api/receptionWorkflows';
import { useNavigationScope, useNavigationScrollRef } from '../../navigation';
import { ReceptionLocaleProvider, useReceptionLocale } from '../../reception/locale';
import '../../styles/reception-content-closure.css';

// Backward compatible props interface
interface ReceptionLayoutProps {
  children: React.ReactNode;
  title?: string;
  noPadding?: boolean;
}

interface DailySummaryResponse {
  already_seen?: boolean;
  notification_date?: string;
}

import {
  ReceptionLayoutContext,
  type ReceptionLayoutContextType,
  type ReceptionLayoutScrollOwner,
} from './ReceptionLayoutContext';
import { receptionNavigationViewKey } from './receptionGroupNavigation';

// Inner component for nested usage
const ReceptionLayoutNested: React.FC<ReceptionLayoutProps & { parentContext: ReceptionLayoutContextType }> = ({ children, title: propTitle, noPadding: propNoPadding, parentContext }) => {
     const { setTitle, setNoPadding } = parentContext;
     useEffect(() => {
        if (propTitle !== undefined) setTitle(propTitle);
     }, [propTitle, setTitle]);

     useEffect(() => {
        if (propNoPadding !== undefined) setNoPadding(propNoPadding);
        // Reset when unmounting? No, that might break other pages.
        // Best to just set what we know.
        return () => {
            if (propNoPadding !== undefined) setNoPadding(false); // Restore default
        };
     }, [propNoPadding, setNoPadding]);

     return <>{children}</>;
};

// Inner component for root usage
const ReceptionLayoutRoot: React.FC<ReceptionLayoutProps> = ({ children, title: propTitle, noPadding: propNoPadding }) => {
  const { language, t } = useReceptionLocale();
  const [title, setTitle] = useState(propTitle || 'Reception');
  const [noPadding, setNoPadding] = useState(propNoPadding || false);
  const [scrollOwner, setScrollOwner] = useState<(ReceptionLayoutScrollOwner & { owner: symbol }) | null>(null);
  const location = useLocation();
  const isLanguageWorkspace = /^\/reception\/(?:language|exams|attempts|materials)(?:\/|$)/.test(location.pathname);
  const navigationScope = useNavigationScope();
  const registerScrollOwner = useCallback<ReceptionLayoutContextType['registerScrollOwner']>((owner, nextScrollOwner) => {
    setScrollOwner({ ...nextScrollOwner, owner });
    return () => {
      setScrollOwner((current) => current?.owner === owner ? null : current);
    };
  }, []);
  const mainScrollRef = useNavigationScrollRef<HTMLElement>({
    key: scrollOwner?.key ?? 'reception.shell-main',
    ready: scrollOwner?.ready ?? true,
    viewKey: scrollOwner?.viewKey ?? receptionNavigationViewKey(
      'reception-shell',
      [location.pathname, location.search],
    ),
  });
  const mainFocus = useScrollableFocus(mainScrollRef);
  const layoutContext = React.useMemo<ReceptionLayoutContextType>(() => ({
    setTitle,
    setNoPadding,
    registerScrollOwner,
    initialized: true,
  }), [registerScrollOwner]);

  // Update state if props change (unlikely for root, but good practice)
  useEffect(() => { if (propTitle) setTitle(propTitle); }, [propTitle]);
  useEffect(() => { if (propNoPadding !== undefined) setNoPadding(propNoPadding); }, [propNoPadding]);

  type PushFn = (msg:string, level?:string)=>void;
  const toastCtx = useToast();
  const push = useCallback<PushFn>((msg, level)=>{
    if(toastCtx && typeof toastCtx.push === 'function') {
      const allowed = ['info','success','warning','danger'] as const;
      type Level = typeof allowed[number];
      const lvl: Level = (level && allowed.includes(level as Level)) ? (level as Level) : 'info';
      toastCtx.push(msg, lvl);
    }
  },[toastCtx]);
  useEffect(()=>{ document.title = title ? `${t(title)} | ${t('Reception')}` : t('Reception'); },[title, t]);

  // Daily notification modal state
  const [showDailyNotification, setShowDailyNotification] = useState(false);
  const presentedNotificationDate = useRef<string | null>(null);
  const dailySummaryQuery = useQuery({
    queryKey: receptionKeys.dailySummary(),
    queryFn: async (): Promise<DailySummaryResponse> => {
      const response = await fetchReceptionDailySummaryResponse();
      if (!response.ok) {
        throw new Error('Failed to load daily summary');
      }
      return (await response.json()) as DailySummaryResponse;
    },
    retry: 0,
    enabled: navigationScope.basename !== '/manager',
    refetchOnWindowFocus: false,
    meta: QUERY_META.sensitive,
  });

  // A late summary must not interrupt a student workflow or another open layer.
  useEffect(() => {
    if (!dailySummaryQuery.data) return;
    if (dailySummaryQuery.data.already_seen) {
      setShowDailyNotification(false);
      return;
    }
    const notificationDate = dailySummaryQuery.data.notification_date ?? 'current';
    if (presentedNotificationDate.current === notificationDate) return;
    const showWhenUnoccupied = () => {
      const occupied = [...document.querySelectorAll<HTMLElement>('[role="dialog"], [role="alertdialog"], [aria-modal="true"]')]
        .some(layer => {
          if (layer.closest('.reception-daily-summary')) return false;
          if (layer.closest('[hidden], [aria-hidden="true"]')) return false;
          for (let node: HTMLElement | null = layer; node; node = node.parentElement) {
            const style = getComputedStyle(node);
            if (style.display === 'none' || style.visibility === 'hidden') return false;
          }
          return true;
        });
      if (!occupied) {
        presentedNotificationDate.current = notificationDate;
        observer.disconnect();
        setShowDailyNotification(true);
      }
    };
    const observer = new MutationObserver(showWhenUnoccupied);
    observer.observe(document.body, { subtree: true, childList: true, attributes: true,
      attributeFilter: ['hidden', 'aria-hidden', 'role', 'aria-modal', 'class', 'style'] });
    showWhenUnoccupied();
    return () => observer.disconnect();
  }, [dailySummaryQuery.data]);

  // Load any server-provided flash messages once
  useEffect(()=>{
    try {
      const el = document.getElementById('flash-data');
      type FlashTuple = [string, string?];
      const win = window as unknown as { __FLASH_MESSAGES__?: FlashTuple[] };
      let msgs: FlashTuple[] | undefined = win.__FLASH_MESSAGES__;
      if(!msgs && el) { msgs = JSON.parse(el.textContent || '[]'); }
      if(Array.isArray(msgs)){
        msgs.forEach(m=>{ if(m && m[0]) push(m[0], (m[1]||'info')); });
        win.__FLASH_MESSAGES__ = []; // prevent re-push
      }
    } catch {/* ignore */}
  },[push]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('reception.sidebar.collapsed') !== 'false'; }
    catch { return true; }
  });
  const toggleSidebar = () => setSidebarCollapsed(current => {
    const next = !current;
    try { localStorage.setItem('reception.sidebar.collapsed', String(next)); } catch { /* Storage may be unavailable. */ }
    return next;
  });
  const collapseSidebar = useCallback(() => {
    if (window.matchMedia('(max-width: 1023px)').matches) setSidebarCollapsed(true);
  }, []);

  // If running under /manager base path, we are likely inside ManagerPageWrapper.
  // In that case, do NOT render the Reception Sidebar and do NOT add left margins.
  const isManagerMode = navigationScope.basename === '/manager';

  if (isManagerMode) {
     return (
        <ReceptionLayoutContext.Provider value={layoutContext}>
             {/* Daily notification could be skipped or kept? Let's keep it but it might look odd if it assumes sidebar. */}
             {showDailyNotification && (
               <DailyNotificationModal onClose={() => setShowDailyNotification(false)} />
             )}

             {/* This main is the real Manager-page scroll owner inside the outer frame. */}
             <main ref={mainFocus.ref} tabIndex={mainFocus.tabIndex} aria-label={t('Reception')} className={`min-h-screen overflow-y-auto bg-white ${isLanguageWorkspace ? 'language-center-workspace' : ''}`}>

                 <div className={`${noPadding ? '' : 'p-6'}`}>
                     {children}
                 </div>
             </main>

             <div
               id="flashMessages"
               className="flash-container fixed top-4 right-4 z-50 max-w-sm space-y-2 pointer-events-none"
             ></div>
        </ReceptionLayoutContext.Provider>
     );
  }

  return (
    <ReceptionLayoutContext.Provider value={layoutContext}>
      <div className={`reception-root h-screen overflow-hidden bg-[#f8fafc] text-slate-950 ${isLanguageWorkspace ? 'language-center-workspace' : ''}`} lang={language} dir={language === 'ar' ? 'rtl' : 'ltr'}>
        {/* Daily notification modal - shown on first login of the day */}
        {showDailyNotification && (
          <DailyNotificationModal onClose={() => setShowDailyNotification(false)} />
        )}

        <div className="flex h-full min-w-0">
          <ReceptionSidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebar}
            onNavigate={collapseSidebar}
          />

          <main ref={mainFocus.ref} tabIndex={mainFocus.tabIndex} aria-label={t('Reception')} className="h-screen min-w-0 flex-1 overflow-y-auto ">

            <div className={`${noPadding ? '' : 'p-6'}`}>
              {children}
            </div>
          </main>
        </div>

        {/* Professional flash messages container */}
        <div
          id="flashMessages"
          className="flash-container fixed top-4 right-4 z-50 max-w-sm space-y-2 pointer-events-none"
        ></div>

      </div>
    </ReceptionLayoutContext.Provider>
  );
};

export const ReceptionLayoutProvider: React.FC<ReceptionLayoutProps> = (props) => {
  const parentContext = React.useContext(ReceptionLayoutContext);

  if (parentContext.initialized) {
     return <ReceptionLayoutNested {...props} parentContext={parentContext} />;
  }

  // Root implementation
  return <ReceptionLocaleProvider><ReceptionLayoutRoot {...props} /></ReceptionLocaleProvider>;
};

export default ReceptionLayoutProvider;
